import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../redux/store';
import { ToggleColorModeButton } from '../../../theme/ToggleColorModeButton';
import { resetPasswordSchema } from '../validation';
import { validateResetToken, resetPasswordWithToken } from '../../../redux/auth';
import { ResetPasswordFormData } from './types';
import {
  AuthContainer,
  FormContainer,
  FormCard,
  Title,
  Subtitle,
  ButtonWrapper,
} from '../styles';
import { ThemeProvider } from 'styled-components';
import AuthBackground from '../LoginPage/AuthBackground';
import ResetPasswordForm from './ResetPasswordForm';
import InvalidTokenView from './InvalidTokenView';

const ResetPasswordPage: React.FC = () => {
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(true);
  const [token, setToken] = useState<string>('');
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const { isResettingPassword } = useSelector((state: RootState) => state.auth);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    // @ts-expect-error - yup resolver type mismatch with react-hook-form
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setIsValidatingToken(true);
      dispatch(validateResetToken(tokenFromUrl)).then((result) => {
        setIsValidatingToken(false);
        if (validateResetToken.fulfilled.match(result)) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      });
    } else {
      setIsValidatingToken(false);
      setIsValidToken(false);
    }
  }, [searchParams, dispatch]);

  const onSubmit = async (data: ResetPasswordFormData): Promise<void> => {
    await dispatch(resetPasswordWithToken({ token, password: data.password, navigate }));
  };

  // @ts-expect-error - react-hook-form type inference issue with yup resolver
  const handleFormSubmit = handleSubmit(onSubmit);

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'dark' | 'light' };

  if (!isValidToken && !isValidatingToken) {
    return (
      <ThemeProvider theme={theme}>
        <AuthContainer>
          <AuthBackground isDarkMode={isDarkMode} />
          
          <ToggleColorModeButton
            width="24px"
            height="24px"
            iconButtonProps={{
              position: 'absolute',
              top: '5',
              right: '5',
              zIndex: 10,
            }}
          />

          <FormContainer>
            <FormCard>
              <InvalidTokenView onBackToLogin={handleBackToLogin} />
            </FormCard>
          </FormContainer>
        </AuthContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <AuthContainer>
        <AuthBackground isDarkMode={isDarkMode} />
        
        <ToggleColorModeButton
          width="24px"
          height="24px"
          iconButtonProps={{
            position: 'absolute',
            top: '5',
            right: '5',
            zIndex: 10,
          }}
        />

        <FormContainer>
          <FormCard>
            {isValidatingToken ? (
              <>
                <Title>Validating Reset Link</Title>
                <Subtitle>Please wait while we validate your reset link...</Subtitle>
              </>
            ) : isValidToken ? (
              <>
                <Title>Reset Your Password</Title>
                <Subtitle>Enter your new password below.</Subtitle>
                
                <ResetPasswordForm
                  control={control as any}
                  errors={errors}
                  onSubmit={handleFormSubmit}
                  isLoading={isResettingPassword}
                />
                
                <ButtonWrapper>
                  <Button
                    icon={<FontAwesomeIcon icon={faArrowLeft} />}
                    type="link"
                    onClick={handleBackToLogin}
                    block
                    style={{
                      marginTop: '16px',
                    }}
                  >
                    Back to Login
                  </Button>
                </ButtonWrapper>
              </>
            ) : null}
          </FormCard>
        </FormContainer>
      </AuthContainer>
    </ThemeProvider>
  );
};

export default ResetPasswordPage;
